class NaptonClassPage extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.form = this.querySelector('[data-class-finder]');
    this.cards = [...this.querySelectorAll('[data-boat-card]')];
    if (!this.form) {
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-filter-base]');
        if (button) this.filterFleet(button.dataset.filterBase);
      }, { signal });
      this.filterFleet('all');
      return;
    }
    this.date = this.form.elements.start_date;
    this.month = this.form.elements.travel_month;
    this.party = this.form.elements.num_persons;
    this.error = this.querySelector('[data-search-error]');
    const now = new Date();
    this.today = this.iso(now);
    this.date.min = this.today;
    this.querySelector('[data-months]').replaceChildren();
    for (let index = 0; index < 24; index++) {
      const month = new Date(now.getFullYear(), now.getMonth() + index, 1, 12);
      const value = this.iso(month).slice(0, 7);
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.month = value;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(month));
      const label = document.createElement('span');
      label.textContent = new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(month);
      const year = document.createElement('small');
      year.textContent = month.getFullYear();
      button.append(label, year);
      this.querySelector('[data-months]').append(button);
    }
    const params = new URLSearchParams(location.search);
    for (const name of ['start_date', 'duration', 'num_persons', 'base_id']) {
      const control = this.form.elements[name];
      const value = params.get(name);
      if (!value) continue;
      if (control instanceof HTMLSelectElement && ![...control.options].some(option => option.value === value)) continue;
      if (name === 'start_date' && !/^\d{4}-\d{2}-\d{2}$/.test(value)) continue;
      if (name === 'num_persons' && (!/^\d+$/.test(value) || Number(value) < 2 || Number(value) > 10)) continue;
      control.value = value;
    }
    if (params.get('date_mode') === 'month') {
      this.form.querySelector('[name="date_mode"][value="month"]').checked = true;
      if (/^\d{4}-\d{2}$/.test(params.get('travel_month') || '')) this.month.value = params.get('travel_month');
    }
    this.form.elements.boat_class.value = this.dataset.tier;
    this.addEventListener('click', event => {
      if (!(event.target instanceof Element)) return;
      const month = event.target.closest('[data-month]');
      if (month) { this.month.value = month.dataset.month; this.syncMode(); this.error.hidden = true; }
      const guest = event.target.closest('[data-guest-change]');
      if (guest) { this.party.value = Math.max(2, Math.min(10, Number(this.party.value || 2) + Number(guest.dataset.guestChange))); this.syncParty(); }
      const base = event.target.closest('[data-filter-base]');
      if (base) this.filterFleet(base.dataset.filterBase);
    }, { signal });
    this.form.addEventListener('change', event => {
      this.error.hidden = true;
      if (event.target.name === 'date_mode') this.syncMode();
      if (event.target.name === 'boat_class' && event.target.value !== this.dataset.tier) {
        const url = new URL(`${window.Shopify?.routes?.root || '/'}pages/${event.target.value}-narrowboats`, location.origin);
        for (const [key, value] of new FormData(this.form)) if (value && key !== 'boat_class') url.searchParams.set(key, value);
        location.assign(url.href);
      }
      if (event.target.name === 'num_persons') this.syncParty();
    }, { signal });
    this.form.addEventListener('submit', event => { event.preventDefault(); this.search(); }, { signal });
    this.syncMode();
    this.syncParty();
    this.filterFleet('all');
    if ('IntersectionObserver' in window && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.observer = new IntersectionObserver(entries => {
        for (const entry of entries) if (entry.isIntersecting) {
          entry.target.classList.remove('nc-reveal-pending');
          entry.target.classList.add('nc-revealed');
          this.observer.unobserve(entry.target);
        }
      }, { threshold: .06 });
      for (const card of this.cards) { card.classList.add('nc-reveal-pending'); this.observer.observe(card); }
    }
  }
  iso(date) { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`; }
  uk(date) { return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getFullYear()).slice(-2)}`; }
  syncMode() {
    const monthly = this.form.querySelector('[name="date_mode"]:checked').value === 'month';
    this.querySelector('[data-month-area]').hidden = !monthly;
    this.month.disabled = !monthly;
    this.date.min = this.today;
    this.date.removeAttribute('max');
    if (monthly && this.month.value) {
      const [year, month] = this.month.value.split('-').map(Number);
      this.date.min = [this.today, `${this.month.value}-01`].sort().at(-1);
      this.date.max = this.iso(new Date(year, month, 0, 12));
      if (this.date.value && !this.date.value.startsWith(this.month.value)) this.date.value = '';
    }
    for (const button of this.querySelectorAll('[data-month]')) button.setAttribute('aria-pressed', String(button.dataset.month === this.month.value));
  }
  syncParty() {
    const value = Math.max(2, Math.min(10, Math.round(Number(this.party.value) || 2)));
    this.party.value = value;
    this.querySelector('[data-guest-change="-1"]').disabled = value <= 2;
    this.querySelector('[data-guest-change="1"]').disabled = value >= 10;
  }
  filterFleet(base) {
    let count = 0;
    for (const card of this.cards) { card.hidden = base !== 'all' && card.dataset.base !== base; if (!card.hidden) count++; }
    for (const button of this.querySelectorAll('[data-filter-base]')) button.setAttribute('aria-pressed', String(button.dataset.filterBase === base));
    this.querySelector('.nc-filter-status[data-fleet-status]').textContent = `${count} / ${this.cards.length}`;
  }
  search() {
    this.syncParty();
    const monthly = this.form.querySelector('[name="date_mode"]:checked').value === 'month';
    if (monthly && !this.month.value) {
      this.error.textContent = this.dataset.monthError;
      this.error.hidden = false;
      this.querySelector('[data-month]')?.focus();
      return;
    }
    if (!this.date.value || !this.date.validity.valid) {
      this.error.textContent = this.dataset.dateError;
      this.error.hidden = false;
      this.date.focus();
      this.date.reportValidity();
      return;
    }
    this.error.hidden = true;
    const base = this.form.elements.base_id.value;
    const guests = Number(this.party.value);
    const matches = this.cards.filter(card => card.dataset.tier === this.dataset.tier && Number(card.dataset.capacity) >= guests && (base === 'Either Base' || base === card.dataset.baseLabel));
    const models = new Map();
    for (const card of matches) {
      const key = `${card.dataset.design}|${card.dataset.base}`;
      if (!models.has(key)) models.set(key, card);
    }
    const output = this.querySelector('[data-model-results]');
    output.replaceChildren();
    const start = new Date(`${this.date.value}T12:00:00`);
    const duration = Number(this.form.elements.duration.value);
    const days = duration === 3 && start.getDay() === 1 ? 4 : duration === 10 && start.getDay() === 1 ? 11 : duration;
    const lastNight = new Date(start);
    lastNight.setDate(start.getDate() + days - 1);
    for (const card of models.values()) {
      const article = document.createElement('article');
      article.className = 'nc-model-result';
      article.dataset.resultTier = this.dataset.tier;
      article.dataset.resultBase = card.dataset.base;
      const heading = document.createElement('h4');
      heading.textContent = card.dataset.design;
      const detail = document.createElement('p');
      detail.textContent = `${card.dataset.baseLabel} · ${new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(start)} · ${this.form.elements.duration.selectedOptions[0].textContent}`;
      const link = document.createElement('a');
      const url = new URL(card.dataset.bookingUrl);
      if (url.protocol !== 'https:' || url.hostname !== 'www.napton-marina.co.uk') continue;
      url.searchParams.set('start_date', this.uk(start));
      url.searchParams.set('end_date', this.uk(lastNight));
      url.searchParams.set('duration', String(duration));
      url.searchParams.set('base_id', card.dataset.baseLabel);
      url.searchParams.set('num_persons', String(guests));
      url.hash = 'holiday_finder';
      link.href = url.href;
      link.textContent = `${this.dataset.liveLabel} ↗`;
      article.append(heading, detail, link);
      output.append(article);
    }
    this.querySelector('[data-result-status]').textContent = models.size ? this.dataset.resultTemplate.replace('[count]', String(models.size)).replace('[class]', this.form.elements.boat_class.selectedOptions[0].textContent) : this.dataset.empty;
    this.querySelector('[data-search-results]').hidden = false;
    this.querySelector('[data-search-results]').scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'nearest' });
  }
  disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
}
if (!customElements.get('napton-class-page')) customElements.define('napton-class-page', NaptonClassPage);


